/* Transport for the supabase-proxy Worker (PostgREST behind Cloudflare).
 *
 * Contract source: legacy/godaddy-package/supabase-client.js. Public reads
 * (countries / cities / districts / plans / listings) take no token; private
 * routes take `Authorization: Bearer <token>` from POST /auth/verify.
 *
 * CORS, as of proxy v3.8.2: the allowlist is no longer a single origin. It
 * accepts souqakar.com and www, the old ejarmasr.com pair during the
 * transition, the Pages production origin, every *.souqakar-web.pages.dev
 * preview and branch deployment, and http://localhost:5173 / 127.0.0.1:5173
 * for local dev. An origin outside that set still gets
 * `Access-Control-Allow-Origin: https://souqakar.com` and is blocked by the
 * browser before a script can read the response.
 *
 * This matters for error reporting only: fetch rejects without a status for
 * both a genuine network failure and a CORS block, so `classify` has to guess
 * which. It guesses from whether THIS origin is on the allowlist -- which is
 * why the predicate below mirrors the worker's rule instead of comparing
 * against one hardcoded origin, as it did before v3.8.2. Getting this wrong
 * does not break a request; it makes the console lie about why one failed.
 */

export const API_ROOT = "https://supabase-proxy.plain-hat-4d25.workers.dev";

/** The origin the proxy falls back to when it refuses the caller's. */
export const ALLOWED_ORIGIN = "https://souqakar.com";

/* Mirrors supabase-proxy v3.8.2's ALLOWED_ORIGINS / ALLOWED_ORIGIN_SUFFIXES.
 * Kept in sync by hand: the worker is Mark's and is read-only to this repo. */
const ALLOWED_ORIGINS = [
  "https://souqakar.com", "https://www.souqakar.com",
  "https://ejarmasr.com", "https://www.ejarmasr.com",
  "https://souqakar-web.pages.dev",
  "http://localhost:5173", "http://127.0.0.1:5173",
];
const ALLOWED_ORIGIN_SUFFIXES = [".plain-hat-4d25.workers.dev", ".souqakar-web.pages.dev"];

export type ApiErrorKind = "cors" | "http" | "network" | "parse";

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number | null;
  readonly origin: string | null;
  readonly path: string;

  constructor(
    kind: ApiErrorKind,
    message: string,
    opts: { status?: number | null; origin?: string | null; path: string },
  ) {
    super(message);
    this.name = "ApiError";
    this.kind = kind;
    this.status = opts.status ?? null;
    this.origin = opts.origin ?? null;
    this.path = opts.path;
  }

  /** Operator-facing one-liner. Used verbatim in the console warning. */
  get report(): string {
    if (this.kind === "cors") {
      return (
        `Blocked by the proxy CORS allowlist. Blocked origin: "${this.origin}". ` +
        `The proxy returns Access-Control-Allow-Origin: ${ALLOWED_ORIGIN} for ` +
        `origins outside its allowlist. Falling back to local fixtures.`
      );
    }
    return `${this.kind.toUpperCase()} on /${this.path}: ${this.message}`;
  }
}

function currentOrigin(): string | null {
  try { return window.location.origin; } catch { return null; }
}

/** True when this browser context is on an origin the proxy's CORS allows. */
export function originIsAllowlisted(): boolean {
  const o = currentOrigin();
  if (!o) return false;
  if (ALLOWED_ORIGINS.includes(o)) return true;
  return o.startsWith("https://") && ALLOWED_ORIGIN_SUFFIXES.some((s) => o.endsWith(s));
}

/**
 * GET a PostgREST path. Returns the parsed array.
 * Throws ApiError; callers decide whether to fall back.
 */
export async function apiGet<T>(path: string, signal?: AbortSignal): Promise<T[]> {
  const url = `${API_ROOT}/${path}`;
  let res: Response;

  try {
    res = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      signal,
    });
  } catch (err) {
    if (signal?.aborted) throw err;
    // fetch rejects without a status for both a genuine network failure and a
    // CORS block — the browser refuses to tell scripts which. On a
    // non-allowlisted origin the CORS block is overwhelmingly the likelier
    // cause, so name it rather than reporting a vague network error.
    const kind: ApiErrorKind = originIsAllowlisted() ? "network" : "cors";
    throw new ApiError(kind, (err as Error)?.message ?? "fetch failed", {
      origin: currentOrigin(),
      path,
    });
  }

  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new ApiError("http", body || res.statusText, {
      status: res.status,
      origin: currentOrigin(),
      path,
    });
  }

  const text = await res.text();
  if (!text) return [];
  try {
    const json: unknown = JSON.parse(text);
    return Array.isArray(json) ? (json as T[]) : ([json] as T[]);
  } catch {
    throw new ApiError("parse", "response was not valid JSON", {
      status: res.status,
      origin: currentOrigin(),
      path,
    });
  }
}

/** Where a rendered dataset actually came from. Drives the mock badge. */
export type DataSource = "live" | "fixture";

/* ── Authed transport ─────────────────────────────────────────────────────
 * Private routes carry the session token as a Bearer. A 401 anywhere means
 * the session is gone: the token is cleared immediately and the app is told,
 * so the visitor lands on login with a return path rather than seeing a
 * half-broken panel. Expiry is discovered on use -- the server is the only
 * thing that knows, exactly as the legacy contract describes.
 */

import { clearSession, getToken } from "@/auth/session";

/** Raised on 401 so callers can distinguish "signed out" from "failed". */
export class UnauthorizedError extends Error {
  constructor() {
    super("session expired");
    this.name = "UnauthorizedError";
  }
}

/** Called once per 401 so the router can route to login. */
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null) {
  onUnauthorized = fn;
}

/* A 401 means two very different things depending on whether we sent a token.
 *
 * WITH a token: the session the server issued is no longer good. Tear it down
 * and route to login -- that is a real expiry.
 *
 * WITHOUT a token: the caller asked a private endpoint as a guest. The server
 * is saying "this needs auth", not "your session ended". There is no session
 * to clear, and yanking the visitor to /login is wrong -- it drags guests off
 * public pages the moment any component happens to ask a /my/* route. That is
 * exactly defect D-W1: the listing detail is public, but the assistant on it
 * asked for the AI quota, and every guest got bounced to login.
 *
 * Callers still get UnauthorizedError either way, so "I could not read this
 * because you are signed out" stays expressible.
 */
function handle401(hadToken: boolean): never {
  if (hadToken) {
    clearSession();
    if (onUnauthorized) onUnauthorized();
  }
  throw new UnauthorizedError();
}

type Body = Record<string, unknown> | Array<Record<string, unknown>>;

async function authedRequest<T>(
  method: "GET" | "POST" | "PATCH" | "DELETE",
  path: string,
  body?: Body,
  opts: { signal?: AbortSignal; representation?: boolean } = {},
): Promise<T[]> {
  const token = getToken();
  const headers: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (opts.representation) headers["Prefer"] = "return=representation";
  if (token) headers["Authorization"] = `Bearer ${token}`;

  let res: Response;
  try {
    res = await fetch(`${API_ROOT}/${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: opts.signal,
    });
  } catch (err) {
    if (opts.signal?.aborted) throw err;
    const kind: ApiErrorKind = originIsAllowlisted() ? "network" : "cors";
    throw new ApiError(kind, (err as Error)?.message ?? "fetch failed", {
      origin: currentOrigin(), path,
    });
  }

  if (res.status === 401) handle401(Boolean(token));

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new ApiError("http", text || res.statusText, {
      status: res.status, origin: currentOrigin(), path,
    });
  }

  const text = await res.text();
  if (!text) return [];
  try {
    const json: unknown = JSON.parse(text);
    return Array.isArray(json) ? (json as T[]) : ([json] as T[]);
  } catch {
    throw new ApiError("parse", "response was not valid JSON", {
      status: res.status, origin: currentOrigin(), path,
    });
  }
}

export const authGet = <T>(p: string, signal?: AbortSignal) =>
  authedRequest<T>("GET", p, undefined, { signal });
export const authPost = <T>(p: string, body: Body, representation = true) =>
  authedRequest<T>("POST", p, body, { representation });
export const authPatch = <T>(p: string, body: Body) =>
  authedRequest<T>("PATCH", p, body, { representation: true });
export const authDelete = <T>(p: string) =>
  authedRequest<T>("DELETE", p);

/** Unauthenticated POST, for the auth endpoints themselves. */
export async function publicPost<T>(path: string, body: Body): Promise<{ status: number; data: T | null }> {
  const res = await fetch(`${API_ROOT}/${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  });
  const text = await res.text().catch(() => "");
  let data: T | null = null;
  try { data = text ? (JSON.parse(text) as T) : null; } catch { data = null; }
  return { status: res.status, data };
}
